import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { perPage } from '~/constants/vdp/ownerReviews';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import StarRatingOwnerReviewContext from '~/context/vdp/StarRatingOwnerReviewContext';
import {
  useGetVehicleDetails,
  useIsNewVehicle
} from '~/context/vdp/VehicleContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import type { OwnerReviewsRequest } from '~/services/vdp/contracts';
import fetchReviews from '~/services/vdp/fetchReviews';
import { generateErrorLogger } from '~/utilities/loggers';
import {
  highlightSearchTerms,
  mapReviewProperties,
  sanitizeReviews
} from '~/utilities/vdp/ownerReviews';
/**
 * Note: Due to how react-hooks/exhaustive-deps treats object properties, we
 * use `obj.fn.call(undefined, args)` in the code below. Destructuring also
 * solves the problem, but explodes the number of constants in scope, which is
 * especially annoying when the constants all have the same name. Hopefully
 * the lint rule will get an option added to it one day.
 *
 * See https://github.com/facebook/react/issues/16265
 */

const useGetReviews = () => {
  const { trackEvent } = useContext(AnalyticsContext);
  const {
    drawerReviews,
    fetchOwnerReviewsFlag,
    hasOwnerReviews: {
      setError: setHasOwnerReviewsError,
      setLoading: setHasOwnerReviewsLoading,
      setLoaded: setHasOwnerReviewsLoaded
    },
    pageNumber,
    pageReviews,
    ratingData,
    searchTerm,
    setDrawerReviewCount,
    setInitialReviewsData,
    starRatingFilter
  } = useContext(StarRatingOwnerReviewContext);
  const { ownerReviews: reviewsResolvedOnServer } = useResolvedOnServer();
  const { make = '', model = '', trim = '', year = 0 } = useGetVehicleDetails();
  const isNewVehicle = useIsNewVehicle();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    reviewsResolvedOnServer
  );
  const isInitialRender = useRef(true);

  // When the server stream populates the section, this hook's fetch is skipped
  // so its `.finally` never clears isInitialRender. Clear it once populated by
  // either path, else the first search re-enters the initial branch and hides
  // the section. A server miss leaves hasEverLoaded false, so the fallback
  // fetch still runs the full initial population.
  useEffect(() => {
    if (pageReviews.hasEverLoaded) {
      isInitialRender.current = false;
    }
  }, [pageReviews.hasEverLoaded]);

  const request = useMemo<OwnerReviewsRequest>(
    () => ({
      make,
      model,
      pageNumber,
      perPage,
      searchTerm,
      starRating: starRatingFilter,
      trim,
      year
    }),
    [make, model, pageNumber, searchTerm, starRatingFilter, trim, year]
  );

  useEffectWithController(
    controller => {
      if (isNewVehicle) {
        setHasOwnerReviewsLoaded();
        ratingData.setLoaded.call(undefined);
        pageReviews.setLoaded.call(undefined);

        return;
      }

      if (shouldSkipFetch()) return;

      drawerReviews.setLoading.call(undefined);

      if (isInitialRender.current) {
        pageReviews.setLoading.call(undefined);
        ratingData.setLoading.call(undefined);
        setHasOwnerReviewsLoading();
      }

      fetchReviews(request, controller)
        .then(({ data }) => {
          if (isInitialRender.current) {
            setInitialReviewsData(data);
          } else {
            const reviews = highlightSearchTerms(
              sanitizeReviews(mapReviewProperties(data.reviews)),
              searchTerm
            );

            setDrawerReviewCount(data.summary.review_count_total);

            if (request.pageNumber > 1) {
              drawerReviews.setData.call(undefined, v => [...v, ...reviews]);
            } else {
              drawerReviews.setData.call(undefined, reviews);
            }
          }
        })
        .catch(
          generateErrorLogger(
            'Fetch owner reviews',
            {},
            {
              callback: (err: Error) => {
                trackEvent('Owner Reviews API failed');
                drawerReviews.setError.call(undefined, err);

                if (isInitialRender.current) {
                  setHasOwnerReviewsError(err);
                  pageReviews.setError.call(undefined, err);
                  ratingData.setError.call(undefined, err);
                }
              },
              controller
            }
          )
        )
        .finally(() => {
          isInitialRender.current = false;
        });
    },
    [
      drawerReviews.setData,
      drawerReviews.setError,
      drawerReviews.setLoading,
      fetchOwnerReviewsFlag,
      isNewVehicle,
      pageReviews.setError,
      pageReviews.setLoaded,
      pageReviews.setLoading,
      ratingData.setError,
      ratingData.setLoaded,
      ratingData.setLoading,
      request,
      searchTerm,
      setDrawerReviewCount,
      setHasOwnerReviewsError,
      setHasOwnerReviewsLoaded,
      setHasOwnerReviewsLoading,
      setInitialReviewsData,
      shouldSkipFetch,
      trackEvent
    ]
  );
};

export default useGetReviews;
